import { Check, Loader2, Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";

interface TestNameEditorProps {
  name: string;
  onSubmit: (name: string) => Promise<boolean> | boolean;
}

export const TestNameEditor: React.FC<TestNameEditorProps> = ({
  name,
  onSubmit,
}) => {
  const [draftName, setDraftName] = useState(name);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraftName(name);
    }
  }, [isEditing, name]);

  const trimmedDraftName = draftName.trim();
  const canSubmit =
    !isSubmitting &&
    trimmedDraftName.length > 0 &&
    trimmedDraftName !== name;

  const handleStartEditing = () => {
    if (isSubmitting) {
      return;
    }

    setDraftName(name);
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    if (isSubmitting) {
      return;
    }

    setDraftName(name);
    setIsEditing(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    try {
      const didSave = await onSubmit(trimmedDraftName);
      if (didSave) {
        setIsEditing(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBlurCapture = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!isEditing || isSubmitting) {
      return;
    }

    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && wrapperRef.current?.contains(nextTarget)) {
      return;
    }

    handleCancelEditing();
  };

  return (
    <div
      ref={wrapperRef}
      className="flex max-w-full items-center gap-2"
      onBlurCapture={handleBlurCapture}
    >
      {isEditing ? (
        <>
          <Input
            aria-label="Test name"
            autoFocus
            className="h-11 min-w-0 max-w-xl text-2xl font-semibold"
            disabled={isSubmitting}
            spellCheck={false}
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void handleSubmit();
              }

              if (event.key === "Escape") {
                event.preventDefault();
                handleCancelEditing();
              }
            }}
          />
          <Button
            type="button"
            size="icon-sm"
            aria-label="Confirm test name"
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            disabled={!canSubmit}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {isSubmitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" />
            )}
          </Button>
        </>
      ) : (
        <>
          <h1 className="truncate text-left text-2xl font-semibold text-foreground">
            {name}
          </h1>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Rename test"
            onClick={handleStartEditing}
          >
            <Pencil className="size-4" />
          </Button>
        </>
      )}
    </div>
  );
};
